import React from 'react';
import { Link } from 'react-router-dom';
import './GameCard.css';

const GameCard = ({ game, type = 'template' }) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return '#4CAF50';
      case 'intermediate': return '#FF9800';
      case 'advanced': return '#F44336';
      default: return '#2196F3';
    }
  };

  const getGameTypeIcon = (gameType) => {
    switch (gameType) {
      case 'quiz':
      case 'QUIZ_RUSH':
        return '🧠';
      case 'matching':
      case 'MEMORY_MATCH':
        return '🔗';
      case 'word_puzzle':
      case 'WORD_BUILDER':
        return '🔤';
      case 'math_challenge':
      case 'FORMULA_RACE':
        return '🔢';
      case 'memory':
        return '🧩';
      case 'drag_drop':
        return '🎯';
      case 'fill_blanks':
        return '📝';
      case 'multiple_choice':
        return '✅';
      case 'CHAIN_REACTION':
        return '⚡';
      default:
        return '🎮';
    }
  };

  const formatSubject = (subject) => {
    return subject.charAt(0).toUpperCase() + subject.slice(1);
  };

  const getProgressPercentage = () => {
    if (type === 'template' && game.userStats) {
      return game.userStats.gamesPlayed > 0 ? 100 : 0;
    }
    if (type === 'custom' && game.userProgress) {
      return game.userProgress.status === 'completed' ? 100 : 
             game.userProgress.status === 'in_progress' ? 50 : 0;
    }
    return 0;
  };

  const getBestScore = () => {
    if (type === 'template' && game.userStats) {
      return game.userStats.bestScore || 0;
    }
    if (type === 'custom' && game.userProgress) {
      return game.userProgress.bestScore || 0;
    }
    return 0;
  };

  const getPlayCount = () => {
    if (type === 'template' && game.userStats) {
      return game.userStats.gamesPlayed || 0;
    }
    if (type === 'custom' && game.analytics) {
      return game.analytics.totalPlays || 0;
    }
    return 0;
  };

  const gameId = game.id || game._id;
  const gameUrl = type === 'template' 
    ? `/games/play/${gameId}` 
    : `/games/play/${gameId}`;

  return (
    <div className="game-card">
      <div className="game-card-header">
        <div className="game-icon">
          {getGameTypeIcon(game.gameType || game.id)}
        </div>
        <div className="game-meta">
          {game.isFeatured && (
            <span className="featured-badge">⭐ Featured</span>
          )}
          {game.difficulty && (
            <span 
              className="difficulty-badge"
              style={{ backgroundColor: getDifficultyColor(game.difficulty) }}
            >
              {game.difficulty}
            </span>
          )}
        </div>
      </div>

      <div className="game-card-content">
        <h3 className="game-title">
          {game.title || game.name}
        </h3>
        <p className="game-description">
          {game.description}
        </p>

        <div className="game-details">
          {game.subject && (
            <div className="game-detail">
              <span className="detail-label">Subject:</span>
              <span className="detail-value">{formatSubject(game.subject)}</span>
            </div>
          )}
          
          {(game.maxQuestions || (game.content && game.content.questions)) && (
            <div className="game-detail">
              <span className="detail-label">Questions:</span>
              <span className="detail-value">
                {game.maxQuestions || game.content.questions.length}
              </span>
            </div>
          )}
          
          {game.timeLimit && (
            <div className="game-detail">
              <span className="detail-label">Time Limit:</span>
              <span className="detail-value">{game.timeLimit}s</span>
            </div>
          )}
        </div>

        <div className="game-stats">
          <div className="stat-item">
            <span className="stat-label">Best Score</span>
            <span className="stat-value">{getBestScore()}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Times Played</span>
            <span className="stat-value">{getPlayCount()}</span>
          </div>
          {type === 'custom' && game.averageRating && (
            <div className="stat-item">
              <span className="stat-label">Rating</span>
              <span className="stat-value">
                ⭐ {game.averageRating.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        {getProgressPercentage() > 0 && (
          <div className="progress-bar">
            <div className="progress-label">Progress</div>
            <div className="progress-track">
              <div 
                className="progress-fill"
                style={{ width: `${getProgressPercentage()}%` }}
              ></div>
            </div>
            <div className="progress-text">{getProgressPercentage()}%</div>
          </div>
        )}
      </div>

      <div className="game-card-footer">
        <Link to={gameUrl} className="play-button">
          {getProgressPercentage() > 0 && getProgressPercentage() < 100 ? 'Continue' : 'Play'}
        </Link>
        
        {type === 'custom' && game.createdBy && (
          <div className="creator-info">
            <span>by {game.createdBy.name}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameCard;